// src/views/Students/StudentStatistics.jsx
import { useEffect, useMemo, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";

import Table from "../../components/Tables/Table";
import { Form, Modal, Select, Space, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import FormButton from "../../components/Buttons/FormButton";
import { FieldArray, Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import NumberInput from "../../components/Inputs/NumberInput";

import { getInstitutions } from "../../services/institutionService";
import { getSessions } from "../../services/sessionService";
import { getStudentCategories } from "../../services/studentCategoryService"; // <-- ensure this exists

import {
  getStudentStatistics,
  deleteStudentStatistics,
  batchUpdateStudentStatistics,
  createStudentStatistics,
} from "../../services/studentStatisticsService";

import { studentStatisticsSchema } from "../../validators/studentStatisticsValidators"; // <-- add batch schema
import { useStateContext } from "../../context/ContextProvider";
import { isInstitutionAdmin } from "../../utils/authHelpers";

const { Option } = Select;

const StudentStatistics = () => {
  const [stats, setStats] = useState([]);
  const [filteredStats, setFilteredStats] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [errs, setErrs] = useState(null);

  const [institutions, setInstitutions] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [categories, setCategories] = useState([]);

  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");
  const [selectedSession, setSelectedSession] = useState("Not Selected");

  const [confirmLoading, setConfirmLoading] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [expandedRowKey, setExpandedRowKey] = useState(null);

  const { user } = useStateContext();
  const isInstAdmin = isInstitutionAdmin(user);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [instRes, sessRes, catRes, statRes] = await Promise.all([
        getInstitutions(),
        getSessions(),
        getStudentCategories(),
        getStudentStatistics(),
      ]);
      setInstitutions(instRes.data?.data || []);
      setSessions(sessRes.data?.data || []);
      setCategories(catRes.data?.data || []);
      setStats(statRes.data?.data || []);
      setFilteredStats(statRes.data?.data || []);
    } catch (e) {
      toast.error("Failed to load student statistics");
    } finally {
      setLoading(false);
    }
  };

  // Filters
  useEffect(() => {
    let filtered = stats;

    if (searchTerm.trim() !== "") {
      filtered = filtered.filter((item) =>
        (item.institution?.name || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    if (selectedSession !== "Not Selected") {
      filtered = filtered.filter(
        (item) => item.session?.id?.toString() === selectedSession?.toString()
      );
    }

    setFilteredStats(filtered);
  }, [searchTerm, stats, selectedInstitution, selectedSession]);

  // Group by Institution + Session (like Graduation)
  const groupedStats = useMemo(() => {
    const grouped = new Map();
    for (const item of filteredStats) {
      const inst = item.institution;
      const sess = item.session;
      if (!inst || !sess) continue;

      const key = `${inst.id}__${sess.id}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          institution: inst,
          session: sess,
          data: [],
          totals: {
            humanities_male_count: 0, // number_of_males
            humanities_female_count: 0, // number_of_females
            sciences_male_count: 0,
            sciences_female_count: 0,
            special_needs: 0,
            total: 0,
          },
        });
      }
      const grp = grouped.get(key);
      grp.data.push(item);

      const humM = item.number_of_males || 0;
      const humF = item.number_of_females || 0;
      const sciM = item.sciences_number_of_males || 0;
      const sciF = item.sciences_number_of_females || 0;
      const spN = item.number_of_students_with_special_needs || 0;

      grp.totals.humanities_male_count += humM;
      grp.totals.humanities_female_count += humF;
      grp.totals.sciences_male_count += sciM;
      grp.totals.sciences_female_count += sciF;
      grp.totals.special_needs += spN;
      grp.totals.total += humM + humF + sciM + sciF;
    }
    return Array.from(grouped.values());
  }, [filteredStats]);

  // Create (batch)
  const onCreate = async (payload, resetForm) => {
    setErrs(null);
    try {
      const { data } = await createStudentStatistics(payload);
      const newRows = data?.data || [];
      const next = [...stats, ...newRows];
      setStats(next);
      setFilteredStats(next);
      toast.success("Student statistics added successfully");
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      const { response } = error || {};
      if (response?.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Failed to add student statistics"
        );
      }
    }
  };

  // Edit -> open modal with that group's rows
  const handleEdit = (group) => {
    setEditingGroup({
      institution: group.institution,
      session: group.session,
      data: group.data, // array with ids
    });
    setIsModalOpen(true);
  };

  // Batch update
  const onBatchUpdate = async (values, resetForm) => {
    setErrs(null);
    try {
      const { data } = await batchUpdateStudentStatistics(values);
      const updated = data?.data || [];

      const mapUpdated = new Map(updated.map((r) => [r.id, r]));
      const replaced = stats.map((row) =>
        mapUpdated.has(row.id) ? mapUpdated.get(row.id) : row
      );
      const newOnes = updated.filter((r) => !stats.find((x) => x.id === r.id));
      const finalState = [...replaced, ...newOnes];

      setStats(finalState);
      setFilteredStats(finalState);

      toast.success("Student statistics updated successfully!");
      setIsModalOpen(false);
      setEditingGroup(null);
      resetForm();
    } catch (error) {
      const { response } = error || {};
      if (response?.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Failed to update student statistics"
        );
      }
    }
  };

  // Delete single stat row
  const confirmDelete = (row) => {
    setRowToDelete(row);
    setIsDeleteModalOpen(true);
  };

  const onDelete = async () => {
    const original = [...stats];
    const next = original.filter((s) => s.id !== rowToDelete.id);
    setStats(next);
    setFilteredStats(next);

    try {
      await deleteStudentStatistics(rowToDelete.id);
      toast.success("Deleted successfully!");
    } catch (e) {
      toast.error("Failed to delete. Try again later.");
      setStats(original);
      setFilteredStats(original);
    } finally {
      setIsDeleteModalOpen(false);
      setRowToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setEditingGroup(null);
    setRowToDelete(null);
    setErrs(null);
  };

  const columns = [
    ...(!isInstAdmin
      ? [
          {
            title: "Institution",
            dataIndex: "institution",
            width: 320,
            render: (institution) => (
              <span className="font-semibold text-lg">{institution?.name}</span>
            ),
          },
        ]
      : []),
    {
      title: "Year",
      dataIndex: ["session", "name"],
      width: 160,
    },
    {
      title: "Male (Humanities)",
      dataIndex: ["totals", "humanities_male_count"],
      width: 160,
    },
    {
      title: "Female (Humanities)",
      dataIndex: ["totals", "humanities_female_count"],
      width: 160,
    },
    {
      title: "Male (Sciences)",
      dataIndex: ["totals", "sciences_male_count"],
      width: 160,
    },
    {
      title: "Female (Sciences)",
      dataIndex: ["totals", "sciences_female_count"],
      width: 160,
    },
    {
      title: "Special Needs",
      dataIndex: ["totals", "special_needs"],
      width: 160,
    },
    {
      title: "Total",
      dataIndex: ["totals", "total"],
      width: 160,
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Student Statistics">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Student Statistics" subtitle1="Student Statistics" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Student Statistics"
            count={groupedStats.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Student Statistics"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={groupedStats}
            expandable={{
              expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
              onExpand: (expanded, record) => {
                setExpandedRowKey(expanded ? record.key : null);
              },
              expandedRowRender: (record) => (
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-2">Student Category</th>
                      <th className="text-left p-2">Male (Humanities)</th>
                      <th className="text-left p-2">Female (Humanities)</th>
                      <th className="text-left p-2">Male (Sciences)</th>
                      <th className="text-left p-2">Female (Sciences)</th>
                      <th className="text-left p-2">Special Needs</th>
                      <th className="text-left p-2">Total</th>
                      <th className="text-left p-2">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-amber-50">
                    {record.data.map((item) => (
                      <tr key={item.id}>
                        <td className="p-2 text-sm">
                          {item.student_category?.name || "—"}
                        </td>
                        <td className="p-2 text-sm">{item.number_of_males}</td>
                        <td className="p-2 text-sm">
                          {item.number_of_females}
                        </td>
                        <td className="p-2 text-sm">
                          {item.sciences_number_of_males}
                        </td>
                        <td className="p-2 text-sm">
                          {item.sciences_number_of_females}
                        </td>
                        <td className="p-2 text-sm">
                          {item.number_of_students_with_special_needs || 0}
                        </td>
                        <td className="p-2 text-sm">
                          {(item.number_of_males || 0) +
                            (item.number_of_females || 0) +
                            (item.sciences_number_of_males || 0) +
                            (item.sciences_number_of_females || 0)}
                        </td>
                        <td className="p-1">
                          <Tooltip title="Delete">
                            <button
                              onClick={() => confirmDelete(item)}
                              className="text-red-500 p-1"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </Tooltip>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ),
            }}
            extra={
              <div className="mb-2 py-2 flex gap-2">
                {!isInstAdmin && (
                  <Select
                    multiple={false}
                    id="institutions"
                    defaultValue={selectedInstitution}
                    className="w-full h-[36px] min-w-[250px]"
                    onChange={(value) => setSelectedInstitution(value)}
                    showSearch
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    <Option value={"Not Selected"}>
                      Filter by Institution
                    </Option>
                    {institutions.map((inst) => (
                      <Option key={inst.id} value={inst.id}>
                        {inst.name}
                      </Option>
                    ))}
                  </Select>
                )}

                <Select
                  multiple={false}
                  id="sessions"
                  defaultValue={selectedSession}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => setSelectedSession(value)}
                  showSearch
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option value={"Not Selected"}>Filter by Year</Option>
                  {sessions.map((s) => (
                    <Option key={s.id} value={s.id}>
                      {s.name}
                    </Option>
                  ))}
                </Select>
              </div>
            }
          />

          {/* Modal */}
          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {editingGroup
                    ? "Edit Student Statistics"
                    : "Add Student Statistics"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
            width={1200}
          >
            <Formik
              initialValues={{
                institution_id: editingGroup
                  ? editingGroup.institution?.id
                  : isInstAdmin
                  ? user?.institution?.id
                  : "",
                session_id: editingGroup ? editingGroup.session?.id : "",
                student_category_stats: editingGroup?.data?.map((row) => ({
                  id: row.id,
                  student_category_id: row.student_category?.id || "",
                  number_of_males: row.number_of_males ?? "",
                  number_of_females: row.number_of_females ?? "",
                  sciences_number_of_males: row.sciences_number_of_males ?? "",
                  sciences_number_of_females:
                    row.sciences_number_of_females ?? "",
                  number_of_students_with_special_needs:
                    row.number_of_students_with_special_needs ?? "",
                })) || [
                  {
                    id: "",
                    student_category_id: "",
                    number_of_males: "",
                    number_of_females: "",
                    sciences_number_of_males: "",
                    sciences_number_of_females: "",
                    number_of_students_with_special_needs: "",
                  },
                ],
              }}
              enableReinitialize
              validationSchema={studentStatisticsSchema}
              onSubmit={(values, { resetForm }) => {
                if (editingGroup) {
                  onBatchUpdate(values, resetForm);
                } else {
                  onCreate(values, resetForm);
                }
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
                setFieldValue,
                submitForm,
              }) => (
                <Form className="space-y-5 px-4! py-4!">
                  {errs && <FormErrors errors={errs} />}

                  <div className="grid gap-3 grid-cols-2">
                    {/* Institution */}
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Institution <span className="text-red-500">*</span>
                      </label>
                      {isInstAdmin ? (
                        <input
                          type="hidden"
                          name="institution_id"
                          value={
                            editingGroup
                              ? editingGroup?.institution?.id
                              : user?.institution?.id
                          }
                        />
                      ) : (
                        <>
                          <Select
                            name="institution_id"
                            value={values.institution_id || undefined}
                            onChange={(val) => {
                              setFieldValue("institution_id", val);
                              // reset session + rows to avoid duplicates
                              setFieldValue("session_id", "");
                              setFieldValue("student_category_stats", [
                                {
                                  student_category_id: "",
                                  number_of_males: "",
                                  number_of_females: "",
                                  sciences_number_of_males: "",
                                  sciences_number_of_females: "",
                                  number_of_students_with_special_needs: "",
                                },
                              ]);
                            }}
                            placeholder="Select Institution"
                            className="w-full"
                            disabled={!!editingGroup}
                          >
                            {institutions.map((inst) => (
                              <Option key={inst.id} value={inst.id}>
                                {inst.name}
                              </Option>
                            ))}
                          </Select>
                          {errors.institution_id && touched.institution_id && (
                            <div className="text-red-500 text-xs">
                              {errors.institution_id}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Session */}
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Year <span className="text-red-500">*</span>
                      </label>
                      <Select
                        name="session_id"
                        value={values.session_id || undefined}
                        onChange={(val) => {
                          setFieldValue("session_id", val);
                          // reset rows when session changes
                          setFieldValue("student_category_stats", [
                            {
                              student_category_id: "",
                              number_of_males: "",
                              number_of_females: "",
                              sciences_number_of_males: "",
                              sciences_number_of_females: "",
                              number_of_students_with_special_needs: "",
                            },
                          ]);
                        }}
                        placeholder="Select Year"
                        className="w-full"
                        disabled={!!editingGroup}
                      >
                        {sessions.map((s) => (
                          <Option key={s.id} value={s.id}>
                            {s.name}
                          </Option>
                        ))}
                      </Select>
                      {errors.session_id && touched.session_id && (
                        <div className="text-red-500 text-xs">
                          {errors.session_id}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Rows */}
                  <FieldArray name="student_category_stats">
                    {({ push, remove }) => (
                      <div className="space-y-4">
                        {values.student_category_stats.map((_, index) => {
                          // enforce unique category per (institution, session)
                          const currentCatId =
                            values.student_category_stats[index]
                              .student_category_id;

                          const usedInForm = values.student_category_stats
                            .map((r, i) => r.student_category_id)
                            .filter((id, i) => i !== index);

                          const existingForCombo = stats
                            .filter(
                              (r) =>
                                r.institution?.id === values.institution_id &&
                                r.session?.id === values.session_id
                            )
                            .map((r) => r.student_category?.id);

                          const excludedIds = new Set([
                            ...usedInForm,
                            ...existingForCombo,
                          ]);

                          const availableCategories = categories.filter(
                            (cat) =>
                              !excludedIds.has(cat.id) ||
                              cat.id === currentCatId // allow current row
                          );

                          return (
                            <div
                              key={index}
                              className="grid md:grid-cols-7 gap-4 items-end border border-gray-200 p-4 rounded-md bg-gray-50"
                            >
                              {/* Category */}
                              <div className="col-span-2">
                                <label className="block mb-1 text-sm font-medium text-gray-700">
                                  Student Category{" "}
                                  <span className="text-red-500">*</span>
                                </label>
                                <Select
                                  value={
                                    values.student_category_stats[index]
                                      .student_category_id || undefined
                                  }
                                  onChange={(val) =>
                                    setFieldValue(
                                      `student_category_stats[${index}].student_category_id`,
                                      val
                                    )
                                  }
                                  placeholder="Select Student Category"
                                  className="w-full"
                                  disabled={
                                    !values.institution_id || !values.session_id
                                  }
                                >
                                  {availableCategories.map((cat) => (
                                    <Option key={cat.id} value={cat.id}>
                                      {cat.name}
                                    </Option>
                                  ))}
                                </Select>
                                {errors.student_category_stats &&
                                  errors.student_category_stats[index]
                                    ?.student_category_id &&
                                  touched.student_category_stats?.[index]
                                    ?.student_category_id && (
                                    <div className="text-red-500 text-xs">
                                      {
                                        errors.student_category_stats[index]
                                          .student_category_id
                                      }
                                    </div>
                                  )}
                              </div>

                              {/* Humanities Male */}
                              <NumberInput
                                name={`student_category_stats[${index}].number_of_males`}
                                label="Male (Humanities)"
                                placeholder="e.g. 120"
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                              />

                              {/* Humanities Female */}
                              <NumberInput
                                name={`student_category_stats[${index}].number_of_females`}
                                label="Female (Humanities)"
                                placeholder="e.g. 130"
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                              />

                              {/* Sciences Male */}
                              <NumberInput
                                name={`student_category_stats[${index}].sciences_number_of_males`}
                                label="Male (Sciences)"
                                placeholder="e.g. 90"
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                              />

                              {/* Sciences Female */}
                              <NumberInput
                                name={`student_category_stats[${index}].sciences_number_of_females`}
                                label="Female (Sciences)"
                                placeholder="e.g. 100"
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                              />

                              {/* Special Needs */}
                              <NumberInput
                                name={`student_category_stats[${index}].number_of_students_with_special_needs`}
                                label="Special Needs"
                                placeholder="e.g. 5"
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                              />

                              {/* Remove new rows only */}
                              {!values.student_category_stats[index].id ? (
                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="text-red-500 justify-self-end"
                                  title="Remove row"
                                >
                                  <FiTrash2 size={18} />
                                </button>
                              ) : (
                                <Tooltip title="Cannot delete existing row here">
                                  <button
                                    type="button"
                                    disabled
                                    className="text-gray-400 justify-self-end cursor-not-allowed"
                                  >
                                    <FiTrash2 size={18} />
                                  </button>
                                </Tooltip>
                              )}
                            </div>
                          );
                        })}

                        {/* Add Category */}
                        {categories.length >
                          values.student_category_stats.length && (
                          <button
                            type="button"
                            className="mt-2 text-blue-600"
                            onClick={() =>
                              push({
                                student_category_id: "",
                                number_of_males: "",
                                number_of_females: "",
                                sciences_number_of_males: "",
                                sciences_number_of_females: "",
                                number_of_students_with_special_needs: "",
                              })
                            }
                            disabled={
                              !values.institution_id || !values.session_id
                            }
                          >
                            + Add Category
                          </button>
                        )}
                      </div>
                    )}
                  </FieldArray>

                  {/* Actions */}
                  <div className="flex justify-end gap-2">
                    <FormCancelButton text="Cancel" onClick={handleCancel} />
                    <FormButton
                      text="Save"
                      type="primary"
                      onClick={submitForm}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </Modal>

          {/* Delete modal */}
          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDelete}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={
              rowToDelete
                ? `Please confirm that you want to delete ${rowToDelete.student_category?.name} stats for ${rowToDelete.institution?.name}`
                : ""
            }
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default StudentStatistics;
